import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Callout, useOverlayContext } from 'seagull';
import { Sync } from 'seagull/icons';
import { useStore, useStoreActions } from '~/contexts';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { getPMDetails } from '~/integrations/PracticeManagement/pm.utils';
import { relationshipModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import PMSyncCompaniesModal from '../PMSyncCompaniesModal/PMSyncCompaniesModal';
import styles from './PracticeManagementSyncFailures.module.scss';
export function PracticeManagementSyncFailures(param) {
    let { relationship, refresh } = param;
    return /*#__PURE__*/ _jsx("div", {
        children: relationshipModel.practiceManagementSyncFailures(relationship).map((failure)=>/*#__PURE__*/ _jsx("div", {
                className: styles.pmSyncFailureContainer,
                children: /*#__PURE__*/ _jsx(PracticeManagementFailureCallout, {
                    failure: failure,
                    relationship: relationship,
                    refresh: refresh
                })
            }, failure.getType()))
    });
}
function PracticeManagementFailureCallout(param) {
    let { failure, relationship, refresh } = param;
    const pmType = failure.practiceManagementType();
    const pmDetails = pmType && getPMDetails(pmType);
    const overlayContext = useOverlayContext();
    const { t: commonTexts } = useLocaleKeys('common');
    const { api } = useStoreActions();
    const showKeeperIntegration = useStore(selectors.integration.showKeeperIntegration);
    const showKarbonIntegration = useStore(selectors.integration.showKarbonIntegration);
    const showClientHubIntegration = useStore(selectors.integration.showClientHubIntegration);
    const showFinancialCentsIntegration = useStore(selectors.integration.showFinancialCentsIntegration);
    const shouldShowFailure = ()=>{
        return failure.accept({
            visitKeeper: ()=>showKeeperIntegration,
            visitKarbon: ()=>showKarbonIntegration,
            visitClientHub: ()=>showClientHubIntegration,
            visitFinancialCents: ()=>showFinancialCentsIntegration
        });
    };
    if (!pmDetails || !shouldShowFailure()) return null;
    const openPMSyncContactsModal = ()=>{
        overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(PMSyncCompaniesModal, {
                    dismissModal: dismissModal,
                    practiceManagementType: pmType,
                    relationship: relationship,
                    getMatchingCompanies: ()=>api.practiceManagement.getMatchingCompanies(pmType, relationshipModel.id(relationship)),
                    getLinkCompany: (companyId)=>api.practiceManagement.linkContact(pmType, relationshipModel.id(relationship), companyId),
                    createNewCompanyAndContact: (relationshipId)=>api.practiceManagement.createNewCompanyAndContact(pmType, relationshipId),
                    refresh: refresh
                });
            }
        });
    };
    const handleDismissSyncFailure = async ()=>{
        overlayContext.startLoader();
        const result = await handleRequest(api.practiceManagement.dismissSyncFailure(pmType, relationshipModel.id(relationship)));
        if (!result.success) {
            overlayContext.showErrorToast(commonTexts.toasts.failedToDismiss(), 'pm-sync-failure-dismiss-btn-error-toast');
            return;
        }
        await refresh({
            silent: true
        });
        overlayContext.stopLoader();
    };
    return /*#__PURE__*/ _jsx(Callout, {
        intent: "error",
        withBorder: true,
        icon: /*#__PURE__*/ _jsx(Sync, {
            size: 18
        }),
        testId: "pm-sync-failure-".concat(pmType),
        withAction: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs(Button, {
                    skin: "primary",
                    onClick: ()=>openPMSyncContactsModal(),
                    testId: "pm-sync-failure-btn-".concat(pmType),
                    children: [
                        "Select ",
                        pmDetails.company
                    ]
                }),
                /*#__PURE__*/ _jsx(Button, {
                    skin: "primaryOutline",
                    onClick: ()=>handleDismissSyncFailure(),
                    testId: "pm-dismiss-sync-failure-btn-".concat(pmType),
                    children: "Dismiss"
                })
            ]
        }),
        children: "There are multiple ".concat(pmDetails.company, " names in ").concat(pmDetails.name, " linked to the contact. To automatically\n            generate ").concat(pmDetails.items, " in ").concat(pmDetails.name, " based on the services in this agreement, please select the\n            correct ").concat(pmDetails.company, ".")
    });
}
